import {
  LayoutDashboard,
  Users,
  ScanLine,
  Settings,
  UserPlus,
  CreditCard,
  Lock,
  Building2,
} from "lucide-react";

export const APP_NAME = process.env.NEXT_PUBLIC_APP_NAME || "LoyeeCards";
export const APP_DESCRIPTION =
  process.env.NEXT_PUBLIC_APP_DESCRIPTION ||
  "Turn one-time visitors into regulars with digital loyalty cards in Apple & Google Wallet. No app, no plastic — launch your rewards program free in minutes.";

export const APP_URL = process.env.NEXTAUTH_URL || "http://localhost:3000";

export const signInDefaultValues = {
  email: "",
  password: "",
};

export const REWARD_EMOJIS = [
  "🎁",
  "☕",
  "🍕",
  "🍺",
  "🍣",
  "🍔",
  "🧁",
  "🍦",
  "✂️",
  "💅",
  "💆",
  "⭐",
  "🍜",
  "🎟️",
  "🛍️",
  "🎉",
  "🧋",
  "💰",
  "🍷",
  "🥯",
] as const;

// ---------------------------------------------------------------------------
// Stamp-layout registry — single source of truth for how many stamps a program
// has AND how the circles are arranged on the card / pass strip.
// `cols` = circles per row. Rows derive from (value + 1 reward) / cols.
// Consumed by lib/passes/apple.ts (Apple strip) and components/LoyaltyCard.tsx
// (web preview). Adding a future size = one entry here + a new Zod literal in
// lib/validators.ts. Keep these in sync.
// ---------------------------------------------------------------------------
export const STAMP_OPTIONS = [
  { value: 4, cols: 5 }, // 4 + reward = 5  → 1 row of 5
  { value: 5, cols: 3 }, // 5 + reward = 6  → 2 rows of 3
  { value: 9, cols: 5 }, // 9 + reward = 10 → 2 rows of 5
] as const;

export type StampValue = (typeof STAMP_OPTIONS)[number]["value"]; // 4 | 5 | 9

// Robust for ANY n — LoyaltyCard is a pure display component used with
// arbitrary values (e.g. the landing playground). Registered values use their
// declared cols; anything else falls back to the legacy 5-wide rule so the
// component can never break on an unregistered value.
export function getStampLayout(pointsRequired: number): {
  cols: number;
  rows: number;
} {
  const total = pointsRequired + 1;
  const cols = STAMP_OPTIONS.find((o) => o.value === pointsRequired)?.cols ?? 5;
  return { cols, rows: Math.ceil(total / cols) };
}

export const registrationDefaultValues = {
  user: { name: "", email: "", password: "", confirmPassword: "" },
  venue: {
    name: "Awesome Cafe",
    phone: "",
    picture: "/images/logo.png",
  },
  card: {
    pointsTarget: 9,
    pointsCollected: 0,
    pointsRequired: 9 as StampValue,
    description: "Free Item",
    background: "#1c1c1e",
    color: "#f5d78e",
    rewardEmoji: "🎁",
  },
};

// Single source of truth for dashboard navigation.
// `vendorOnly: true` means the item is hidden for STAFF users.
// `adminOnly: true` means the item is shown ONLY to ADMIN (the Vendors panel).
export const dashboardNavItems = [
  {
    href: "/dashboard/scanner",
    label: "Scanner",
    icon: ScanLine,
    vendorOnly: false,
    adminOnly: false,
  },
  {
    href: "/dashboard",
    label: "Overview",
    icon: LayoutDashboard,
    vendorOnly: true,
    adminOnly: false,
  },
  {
    href: "/dashboard/customers",
    label: "Customers",
    icon: Users,
    vendorOnly: true,
    adminOnly: false,
  },
  {
    href: "/dashboard/settings",
    label: "Settings",
    icon: Settings,
    vendorOnly: true,
    adminOnly: false,
  },
  {
    href: "/dashboard/staff",
    label: "Staff",
    icon: UserPlus,
    vendorOnly: true,
    adminOnly: false,
  },
  {
    href: "/dashboard/billing",
    label: "Billing",
    icon: CreditCard,
    vendorOnly: true,
    adminOnly: false,
  },
  {
    href: "/dashboard/vendors",
    label: "Vendors",
    icon: Building2,
    vendorOnly: false,
    adminOnly: true,
  },
  {
    href: "/dashboard/account",
    label: "Account",
    icon: Lock,
    vendorOnly: false,
    adminOnly: false,
  },
];
